// ── Presença amostrada (painel Uso da equipe) ─────────────────────
// O token do Supabase renova sozinho, então "quantidade de logins" não
// mede uso: um colaborador diário pode ter um único login em meses. A
// presença aqui é AMOSTRADA — o app avisa o banco uma vez por janela.

/**
 * Tamanho da janela de presença, em minutos. Cada ping credita uma janela
 * inteira, mesmo que a pessoa tenha ficado só um pedaço dela.
 *
 * ponytail: mudar este valor torna o histórico anterior incomparável — as
 * linhas antigas de `user_active_days` ficam noutra escala e não há como
 * distinguir. Se precisar mudar, migrar os dados ou versionar a coluna.
 */
export const USAGE_PING_MINUTES = 15;

/** Presença aproximada em horas, a partir do total de pings do período. */
export const pingsToHours = (pings: number): number =>
  (Math.max(pings, 0) * USAGE_PING_MINUTES) / 60;

/** `6.33` → `"6h20"`. Zero vira travessão, não `"0h00"`. */
export const formatHours = (hours: number): string => {
  if (!Number.isFinite(hours) || hours <= 0) return '—';
  const total = Math.round(hours * 60);
  return `${Math.floor(total / 60)}h${String(total % 60).padStart(2, '0')}`;
};

export type PresenceStatus = 'hoje' | 'recente' | 'ausente' | 'sumido' | 'nunca';

/**
 * Classifica presença pelo último acesso. `firstAccess` vem de `profiles` com
 * polaridade invertida: true = nunca entrou (markFirstAccessDone grava false
 * no primeiro login por senha).
 *
 * `nowISO` é injetado para o cálculo ser testável e não depender do relógio.
 */
export const classifyPresence = (
  ultimoAcessoISO: string | null,
  firstAccess: boolean,
  nowISO: string,
): PresenceStatus => {
  if (firstAccess) return 'nunca';
  if (!ultimoAcessoISO) return 'sumido';

  const dias = daysSince(ultimoAcessoISO, nowISO);
  if (dias === null) return 'sumido';
  if (dias < 1) return 'hoje';
  if (dias <= 2) return 'recente';
  if (dias <= 7) return 'ausente';
  return 'sumido';
};

/** Dias inteiros decorridos entre dois instantes ISO. Null se a data for inválida. */
export const daysSince = (fromISO: string, nowISO: string): number | null => {
  const from = Date.parse(fromISO);
  const now = Date.parse(nowISO);
  if (Number.isNaN(from) || Number.isNaN(now)) return null;
  return Math.floor((now - from) / 86_400_000);
};

/** "hoje", "ontem", "há 5 dias" — rótulo curto do último acesso. */
export const lastAccessLabel = (
  ultimoAcessoISO: string | null,
  firstAccess: boolean,
  nowISO: string,
): string => {
  if (firstAccess) return 'nunca entrou';
  if (!ultimoAcessoISO) return 'sem registro';
  const dias = daysSince(ultimoAcessoISO, nowISO);
  if (dias === null) return 'sem registro';
  if (dias < 1) return 'hoje';
  if (dias === 1) return 'ontem';
  return `há ${dias} dias`;
};

/**
 * Dias úteis (seg–sex) no período de `days` dias que termina hoje. É o
 * denominador de "18 de 22" — o numerador vem da RPC.
 */
export const businessDaysIn = (days: number, nowISO: string): number => {
  const end = new Date(nowISO);
  if (Number.isNaN(end.getTime())) return 0;
  let count = 0;
  for (let i = 0; i < Math.max(days, 0); i += 1) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return count;
};
